import { RefObject, useEffect } from "react"

function useClickOutside<T extends HTMLElement = HTMLElement>(params: {
  ref: RefObject<T>
  onClickOutside: (event: MouseEvent) => void
  insideElementsIds?: string[] //elements that technically go outside, but should be considered as being an inside click
  disableScrollBarClick?: boolean
}) {
  const { ref, onClickOutside, insideElementsIds, disableScrollBarClick } =
    params
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      const el = ref?.current
      const target = event.target as Node
      let shouldReturn = false

      if (disableScrollBarClick) {
        const targetHTMLElement = event.target as HTMLElement
        const targetOffsetHeight = targetHTMLElement.offsetHeight
        const targetOffsetWidth = targetHTMLElement.offsetWidth
        const eventOffsetHeight = event.offsetY
        const eventOffsetWidth = event.offsetX

        // Do nothing if clicking the part of the element, where scrollbar is expected to be placed
        if (
          targetOffsetHeight - eventOffsetHeight <= 10 ||
          targetOffsetWidth - eventOffsetWidth <= 10
        ) {
          shouldReturn = true
        }
      }

      // Do nothing if clicking ref's element or descendent elements
      if (!el || el.contains(target)) {
        shouldReturn = true
      }

      // Do nothing if elements from insideElementsIds or descendent elements are clicked
      if (insideElementsIds && insideElementsIds.length) {
        insideElementsIds.forEach((id) => {
          const element = document.getElementById(id)
          if (element && element.contains(target)) {
            shouldReturn = true
          }
        })
      }

      if (shouldReturn) {
        return
      }

      onClickOutside(event)
    }

    document.addEventListener("mousedown", listener)
    document.addEventListener("touchstart", listener)

    return () => {
      document.removeEventListener("mousedown", listener)
      document.removeEventListener("touchstart", listener)
    }
  }, [ref, onClickOutside, insideElementsIds, disableScrollBarClick])
}

export default useClickOutside
